import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import SelectTextField from '../../molecules/SelectTextField';
import { contractApi } from '../../../services/contractApi';
import type { RoleRequirementDto, ContractStructureDto } from '../../../services/contractApi';
import AuditHistoryPanel from './AuditHistoryPanel';

const SIGNER_ROLES = [
    { value: 'EMITENTE', label: 'Emitente' },
    { value: 'TESTEMUNHA', label: 'Testemunha' },
    { value: 'VALIDADOR', label: 'Validador' },
    { value: 'AVALISTA', label: 'Avalista' },
    { value: 'AVALISTA_PF', label: 'Avalista PF' },
    { value: 'AVALISTA_PJ', label: 'Avalista PJ' },
    { value: 'CEDENTE', label: 'Cedente' },
    { value: 'CESSIONARIA', label: 'Cessionária' },
    { value: 'CREDORA', label: 'Credora' },
    { value: 'CREDORA_FIDUCIARIA', label: 'Credora Fiduciária' },
    { value: 'DEVEDOR_FIDUCIARIO', label: 'Devedor Fiduciário' },
    { value: 'INTERVENIENTE_ANUENTE', label: 'Interveniente Anuente' },
];

const PARTICIPANT_SOURCES = [
    { value: 'RECIPIENT', label: 'Cessionária (RECIPIENT)' },
    { value: 'ASSIGNOR', label: 'Cedente (ASSIGNOR)' },
    { value: 'ANY', label: 'Qualquer (ANY)' },
];

interface FormRow {
    id: string;
    role: string;
    label: string;
    minCount: number;
    participantSource: RoleRequirementDto['participantSource'];
    signingOrder: number;
}

const generateId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const createEmptyRow = (): FormRow => ({
    id: generateId(),
    role: '',
    label: '',
    minCount: 1,
    participantSource: 'ANY',
    signingOrder: 1,
});

const ContractTypeStructureForm = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(name !== 'new');
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<FormRow[]>([createEmptyRow()]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [newTypeName, setNewTypeName] = useState('');
    const isCreateMode = name === 'new';

    useEffect(() => {
        if (name && !isCreateMode) loadContractType();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const loadContractType = async () => {
        setLoading(true);
        try {
            const ct = await contractApi.getContractType(name!);
            if (ct.structures && ct.structures.length > 0) {
                const roles = ct.structures[0].roles || [];
                setRows(roles.map(r => ({
                    id: generateId(),
                    role: r.role,
                    label: r.label,
                    minCount: r.minCount,
                    participantSource: r.participantSource,
                    signingOrder: r.signingOrder,
                })));
            } else {
                setRows([createEmptyRow()]);
            }
        } catch (e: any) {
            toast.error(e.message || 'Erro ao carregar tipo de contrato');
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setRows(prev => [...prev, createEmptyRow()]);
    };

    const handleRemoveRow = (id: string) => {
        setRows(prev => prev.filter(r => r.id !== id));
        setErrors(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (key.startsWith(id)) delete next[key];
            });
            return next;
        });
    };

    const handleFieldChange = (id: string, field: keyof FormRow, value: string | number) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        // Clear error on change
        setErrors(prev => {
            const next = { ...prev };
            delete next[`${id}.${field}`];
            return next;
        });
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (rows.length === 0) {
            toast.error('Pelo menos um papel é obrigatório');
            return false;
        }
        const seenRoles = new Set<string>();
        rows.forEach((row) => {
            if (!row.role) newErrors[`${row.id}.role`] = 'Obrigatório';
            if (!row.label) newErrors[`${row.id}.label`] = 'Obrigatório';
            if (row.minCount < 1) newErrors[`${row.id}.minCount`] = 'Mínimo 1';
            if (row.signingOrder < 1) newErrors[`${row.id}.signingOrder`] = 'Mínimo 1';
            if (row.role && seenRoles.has(row.role)) {
                newErrors[`${row.id}.role`] = 'Papel duplicado';
            }
            if (row.role) seenRoles.add(row.role);
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        if (isCreateMode && !newTypeName.trim()) {
            toast.error('Nome do tipo de contrato é obrigatório');
            return;
        }
        setSaving(true);
        try {
            const structures: ContractStructureDto[] = [{
                name: 'primary',
                roles: rows.map(r => ({
                    role: r.role,
                    label: r.label,
                    minCount: r.minCount,
                    required: true,
                    participantSource: r.participantSource,
                    signingOrder: r.signingOrder,
                })),
            }];
            const typeName = isCreateMode ? newTypeName.trim().toUpperCase().replace(/\s+/g, '_') : name!;
            if (isCreateMode) {
                await contractApi.createContractType(typeName, structures);
                toast.success('Tipo de contrato criado com sucesso!');
                navigate(`/contract-types/${typeName}`);
            } else {
                await contractApi.updateContractTypeStructures(typeName, structures);
                toast.success('Estrutura salva com sucesso!');
            }
        } catch (e: any) {
            if (e.message) {
                toast.error(e.message);
            } else {
                toast.error('Erro ao salvar estrutura');
            }
        } finally {
            setSaving(false);
        }
    };

    // Group rows by signingOrder for visual display
    const groupedRows = useMemo(() => {
        const groups: Record<number, FormRow[]> = {};
        rows.forEach(row => {
            const order = row.signingOrder || 1;
            if (!groups[order]) groups[order] = [];
            groups[order].push(row);
        });
        return Object.entries(groups)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([order, items]) => ({ order: Number(order), items }));
    }, [rows]);

    const getError = (rowId: string, field: string) => errors[`${rowId}.${field}`];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {loading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-6xl mx-auto">
                    <button onClick={() => navigate('/contract-types')} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {isCreateMode ? 'Novo Tipo de Contrato' : `Estrutura: ${name}`}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Configuração dos papéis de assinantes</p>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
                {/* Name field (create mode only) */}
                {isCreateMode && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Identificação</h2>
                        <div className="max-w-md">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nome do Tipo (ex: CESSAO_CREDITO)</label>
                            <input
                                type="text"
                                value={newTypeName}
                                onChange={e => setNewTypeName(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 dark:text-white"
                                placeholder="Ex: CESSAO_CREDITO"
                            />
                        </div>
                    </div>
                )}

                {/* Form */}
                {!loading && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Papéis de Assinante</h2>
                            <button
                                onClick={handleAddRow}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                <FiPlus className="w-4 h-4" />
                                Adicionar Papel
                            </button>
                        </div>

                        {/* Grouped display */}
                        {groupedRows.map(group => (
                            <div key={group.order} className="mb-6 last:mb-0">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                                        Ordem {group.order}
                                    </span>
                                    <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700" />
                                </div>

                                <div className="space-y-3">
                                    {group.items.map(row => (
                                        <div key={row.id} className="grid grid-cols-12 gap-3 items-start p-3 rounded-lg border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                                            {/* Role */}
                                            <div className="col-span-3">
                                                <SelectTextField
                                                    id={`role-${row.id}`}
                                                    name={`role-${row.id}`}
                                                    label="Papel"
                                                    value={row.role}
                                                    onChange={e => handleFieldChange(row.id, 'role', e.target.value)}
                                                    items={SIGNER_ROLES}
                                                    emptyLabel="Selecione..."
                                                    errorMessage={getError(row.id, 'role')}
                                                />
                                            </div>

                                            {/* Label */}
                                            <div className="col-span-3">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Rótulo</label>
                                                <input
                                                    type="text"
                                                    value={row.label}
                                                    onChange={e => handleFieldChange(row.id, 'label', e.target.value)}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'label') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                    placeholder="Ex: Emitente"
                                                />
                                                {getError(row.id, 'label') && <span className="text-xs text-red-500">{getError(row.id, 'label')}</span>}
                                            </div>

                                            {/* Min Count */}
                                            <div className="col-span-1">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mín.</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={row.minCount}
                                                    onChange={e => handleFieldChange(row.id, 'minCount', Math.max(1, parseInt(e.target.value) || 1))}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'minCount') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                />
                                                {getError(row.id, 'minCount') && <span className="text-xs text-red-500">{getError(row.id, 'minCount')}</span>}
                                            </div>

                                            {/* Participant Source */}
                                            <div className="col-span-3">
                                                <SelectTextField
                                                    id={`source-${row.id}`}
                                                    name={`source-${row.id}`}
                                                    label="Fonte"
                                                    value={row.participantSource}
                                                    onChange={e => handleFieldChange(row.id, 'participantSource', e.target.value)}
                                                    items={PARTICIPANT_SOURCES}
                                                    emptyLabel="Selecione..."
                                                />
                                            </div>

                                            {/* Signing Order */}
                                            <div className="col-span-1">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ordem</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={row.signingOrder}
                                                    onChange={e => handleFieldChange(row.id, 'signingOrder', Math.max(1, parseInt(e.target.value) || 1))}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'signingOrder') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                />
                                                {getError(row.id, 'signingOrder') && <span className="text-xs text-red-500">{getError(row.id, 'signingOrder')}</span>}
                                            </div>

                                            {/* Remove */}
                                            <div className="col-span-1 flex items-end pb-1">
                                                <button
                                                    onClick={() => handleRemoveRow(row.id)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                                    title="Remover"
                                                >
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {rows.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                Nenhum papel configurado. Clique em "Adicionar Papel" para começar.
                            </p>
                        )}

                        {/* Save button */}
                        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Salvando...' : 'Salvar Estrutura'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Audit History */}
                {!loading && name && !isCreateMode && <AuditHistoryPanel contractTypeName={name} />}
            </div>
        </div>
    );
};

export default ContractTypeStructureForm;
